import isomorphicFetch from 'isomorphic-fetch'
import { timeout as fetchWithTimeout } from 'promise-timeout'
import qs from 'qs'

import { isServer } from '@library/environment'
import { getServerEnvConfig } from '@library/environment/server'

import { JSONParse, processJSON } from 'utils/jsonHelper'

import { isProd } from './isomorphicEnvironment'

const DEFAULT_TIME_OUT = 50000
const STATUS_CODES_WITH_NO_CONTENT: number[] = [204]

export const dataDefault: Record<string, any> = {}
export const methodDefault = 'GET' as const
export const cacheDefault: RequestCache = 'default'
export const headerDefault: Record<string, string> = {}
export const timeoutDefault: number | null = null
export const includeCookiesDefault = false
export const useMenuServiceDefault = false
export const useOverwriteRequestMethodDefault = true

type RequestDetails = RequestInit & {
  headers: Record<string, string>
  timeout?: number | null
}

type FetchMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | string

export const fetch = <T>(
  accessToken: string | null = '',
  url: string,
  data: Record<string, any> = dataDefault,
  method: FetchMethod = methodDefault,
  cache: RequestCache = cacheDefault,
  headers: Record<string, string> = headerDefault,
  timeout: number | null = timeoutDefault,
  includeCookies: boolean = includeCookiesDefault,
  useMenuService: boolean = useMenuServiceDefault,
  useOverwriteRequestMethod: boolean = useOverwriteRequestMethodDefault,
): Promise<T> => {
  const requestData: Record<string, any> = {
    ...data,
  }

  let httpMethod = String(method || '').toUpperCase()
  if (useOverwriteRequestMethod && (method === 'PUT' || method === 'PATCH')) {
    requestData._method = httpMethod
    httpMethod = 'POST'
  }

  let body = ''
  let requestUrl = url || ''
  if (requestUrl[requestUrl.length - 1] === '/') {
    requestUrl = requestUrl.substr(0, requestUrl.length - 1)
  }

  let requestHeaders: Record<string, string> = headers || {}
  let queryString: string | undefined

  if (httpMethod === 'GET') {
    queryString = qs.stringify(requestData)
    if (queryString) {
      requestUrl += `?${queryString}`
    }
  } else {
    const contentType = requestHeaders['Content-Type']
    const isContentTypeJSON = contentType === 'application/json'

    body = isContentTypeJSON ? JSON.stringify(requestData) : qs.stringify(requestData)

    if (!contentType) {
      requestHeaders = {
        ...requestHeaders,
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    }
  }

  if (accessToken) {
    requestHeaders = { ...requestHeaders, Authorization: `Bearer ${accessToken}` }
  }

  if (isServer() && isProd()) {
    requestHeaders['API-Token'] = getServerEnvConfig().API_TOKEN || ''
  }

  const requestDetails: RequestDetails = {
    method: httpMethod,
    headers: requestHeaders,
    cache,
  }

  if (includeCookies) {
    requestDetails.credentials = 'same-origin'
  }

  if (cache === 'no-store' || cache === 'reload' || cache === 'no-cache') {
    requestUrl += queryString ? '&' : '?'
    requestUrl += `_=${Date.now()}`
  }

  if (body) {
    requestDetails.body = body
  }

  if (timeout) {
    requestDetails.timeout = timeout
  }

  let responseStatus: number | undefined
  let responseRedirected: boolean | undefined
  let responseUrl: string | undefined

  const fetchPromise = isomorphicFetch(requestUrl, requestDetails)

  return (
    fetchWithTimeout(fetchPromise, timeout || DEFAULT_TIME_OUT)
      .then((response: Response) => {
        const { status, redirected, url: endpointOrRedirectedUrl } = response
        responseStatus = status
        responseRedirected = redirected
        responseUrl = endpointOrRedirectedUrl

        return response
      })
      .then((response) => response.text())
      .then((responseText: string) => {
        if (
          !responseText &&
          responseStatus &&
          STATUS_CODES_WITH_NO_CONTENT.includes(responseStatus)
        ) {
          return [{}, responseStatus] as [any, number]
        }

        return [JSONParse(responseText, useMenuService), responseStatus] as [any, number]
      }) // eslint-disable-line new-cap
      .then(processJSON) /* TODO - try refresh auth token and repeat request if successful */
      .then(({ response, meta }: any) => {
        if (useMenuService) {
          return {
            data: response.data,
            included: response.included,
            meta: response.meta,
          }
        }

        return { data: response, meta }
      }) as Promise<T>
  ).catch((e) => {
    const fallbackMessage = 'Unknown error occurred during fetch request'
    const message =
      (e && typeof e.message === 'string' && e.message.trim()) ||
      (typeof e === 'string' && e.trim()) ||
      fallbackMessage

    // Enrich the existing error object, preserving its stack trace
    type FetchError = Error & {
      code?: number
      errors?: any[]
      status?: number
      redirected?: boolean
      errorDetails?: any
      url?: string
    }

    const err = (e && typeof e === 'object' ? e : new Error(message)) as FetchError
    err.code = e?.code || 500
    err.errors = e?.errors || []
    err.status = responseStatus || 500
    err.redirected = responseRedirected
    err.errorDetails = e?.errorDetails
    err.url = responseUrl
    throw err
  })
}

type FetchRawOptions = {
  accessToken?: string | null
  method?: FetchMethod
  cache?: RequestCache
  headers?: Record<string, string>
  timeout?: number | null
  includeCookies?: boolean
  useMenuService?: boolean
  useOverwriteRequestMethod?: boolean
}

export function fetchRaw(
  url: string,
  data: Record<string, any> = {},
  options: FetchRawOptions,
): Promise<any> {
  return fetch(
    options.accessToken,
    url,
    data,
    options.method,
    options.cache,
    options.headers,
    options.timeout || null,
    !!options.includeCookies,
    !!options.useMenuService,
    options.useOverwriteRequestMethod !== false,
  )
}
